import type { BadgeProps } from '@/components/ui/badge';
import type {
  CampaignStatus,
  Confidentiality,
  DocumentCategory,
  MarketingChannel,
  SignatureStatus,
} from './types';

type Variant = BadgeProps['variant'];

export const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  CONTRACT: 'Contract',
  PROPOSAL: 'Proposal',
  SOW: 'Statement of work',
  NDA: 'NDA',
  INVOICE: 'Invoice',
  DESIGN: 'Design',
  TECHNICAL_SPEC: 'Technical spec',
  CREDENTIAL: 'Credential',
  REPORT: 'Report',
  ID_DOCUMENT: 'ID document',
  OTHER: 'Other',
};

export const CONFIDENTIALITY_LABELS: Record<Confidentiality, string> = {
  PUBLIC: 'Public',
  INTERNAL: 'Internal',
  CONFIDENTIAL: 'Confidential',
  RESTRICTED: 'Restricted',
};

export const CONFIDENTIALITY_VARIANT: Record<Confidentiality, Variant> = {
  PUBLIC: 'outline',
  INTERNAL: 'secondary',
  CONFIDENTIAL: 'warning',
  RESTRICTED: 'destructive',
};

/** What each level actually means, shown where someone is choosing one. */
export const CONFIDENTIALITY_HINTS: Record<Confidentiality, string> = {
  PUBLIC: 'Anyone in the workspace, and shareable without a password.',
  INTERNAL: 'Anyone in the workspace who can see documents.',
  CONFIDENTIAL: 'Anyone with document access, but worth thinking before sharing.',
  RESTRICTED: 'Nobody, until they are granted it by name. Sharing needs a password.',
};

export const SIGNATURE_STATUS_LABELS: Record<SignatureStatus, string> = {
  DRAFT: 'Not sent',
  SENT: 'Awaiting signature',
  PARTIALLY_SIGNED: 'Partly signed',
  COMPLETED: 'Signed',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

export const SIGNATURE_STATUS_VARIANT: Record<SignatureStatus, Variant> = {
  DRAFT: 'outline',
  SENT: 'secondary',
  PARTIALLY_SIGNED: 'warning',
  COMPLETED: 'success',
  DECLINED: 'destructive',
  EXPIRED: 'outline',
  CANCELLED: 'outline',
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  SENDING: 'Sending',
  SENT: 'Sent',
  PAUSED: 'Paused',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
};

export const CAMPAIGN_STATUS_VARIANT: Record<CampaignStatus, Variant> = {
  DRAFT: 'outline',
  SCHEDULED: 'secondary',
  SENDING: 'default',
  SENT: 'success',
  PAUSED: 'warning',
  CANCELLED: 'outline',
  FAILED: 'destructive',
};

export const CHANNEL_LABELS: Record<MarketingChannel, string> = {
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
};

export const SUPPRESSION_REASON_LABELS: Record<string, string> = {
  UNSUBSCRIBED: 'Unsubscribed',
  HARD_BOUNCE: 'Hard bounce',
  SOFT_BOUNCE_LIMIT: 'Too many soft bounces',
  COMPLAINED: 'Marked as spam',
  DO_NOT_CONTACT: 'Do not contact',
  MANUAL: 'Added by hand',
};

export const RECIPIENT_STATUS_LABELS: Record<string, string> = {
  QUEUED: 'Queued',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  OPENED: 'Opened',
  CLICKED: 'Clicked',
  BOUNCED: 'Bounced',
  COMPLAINED: 'Complained',
  UNSUBSCRIBED: 'Unsubscribed',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

export const STEP_LABELS: Record<string, string> = {
  wait: 'Wait',
  send_email: 'Send an email',
  send_whatsapp: 'Send a WhatsApp template',
  add_tag: 'Add a tag',
  remove_tag: 'Remove a tag',
  create_task: 'Create a task',
  notify_user: 'Notify a colleague',
  condition: 'Branch on a condition',
  exit: 'Leave the journey',
};

/** Why someone was left out of a send, in the words a person would use. */
export const SKIP_LABELS: Record<string, string> = {
  noAddress: 'no address on file',
  noConsent: 'has not consented',
  suppressed: 'on the suppression list',
  excluded: 'excluded by hand',
  frequencyCapped: 'had their week of messages already',
};

/** "1536000" -> "1.5 MB". */
export function formatBytes(bytes: string | number): string {
  const value = Number(bytes);
  if (!Number.isFinite(value)) return '—';
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = value / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${Math.round(size * 10) / 10} ${units[unit]}`;
}
